// tslint:disable:max-file-line-count
import cn from 'classnames';
import React, {
  createRef,
  PropsWithChildren,
  useCallback,
  useEffect,
  useState
} from 'react';
import { BasicCheckbox } from '../basic-inputs';
import { deepEquals } from '../common/Helper';
import { usePrevious } from '../common/Hooks';
import { FilteredArrayOfReactElement } from '../common/Types';
import { Spinner } from '../spinner';
import {
  ColumnProps,
  ColumnsProps,
  DatagridColumn,
  FilterProps
} from './DatagridColumn';
import { FooterProps, PaginationProps } from './DatagridFooter';
import { RowsProps } from './DatagridRow';
import { DetailPaneContainerProps } from './DetailPane';
import {
  ColumnFilterState,
  ColumnSortState,
  FilterStateChange,
  MultiSelectionProps,
  PaginationStateChange,
  SingleSelectionProps,
  SortStateChange
} from './types';

interface DatagridProps {
  loading?: boolean;
  title?: string;
  columns: React.ReactElement<ColumnsProps>;
  rows: React.ReactElement<RowsProps>;
  placeholder?: React.ReactElement<PlaceholderProps>;
  footer?: React.ReactElement<FooterProps>;
  onStateChange?: (
    pagination: PaginationStateChange,
    filter: FilterStateChange,
    sort: SortStateChange
  ) => void;
  expandable?: boolean;
  multiSelection?: MultiSelectionProps;
  singleSelection?: SingleSelectionProps;
  detailPane?: React.ReactElement<DetailPaneContainerProps>;
}

export const Datagrid = React.forwardRef<HTMLDivElement, DatagridProps>(
  (props, ref) => {
    const datagridTableRef = createRef<HTMLDivElement>();
    const [height, setHeight] = useState<number>();
    const [detailPaneStatus, setDetailPaneStatus] = useState({
      status: false,
      key: ''
    });

    const {
      title,
      columns,
      expandable,
      footer,
      loading,
      placeholder,
      rows,
      multiSelection,
      onStateChange,
      singleSelection,
      detailPane
    } = props;

    const prevRows: typeof rows | undefined = usePrevious(rows);

    const filteredColumnChildren = React.Children.toArray(
      columns.props.children
    ).filter(
      child => child !== undefined && child !== null
    ) as FilteredArrayOfReactElement<ColumnProps>;

    useEffect(() => {
      const rowsChildren = rows.props.children;
      const prevRowsChildren = prevRows && prevRows.props.children;
      if (Array.isArray(rowsChildren)) {
        if (
          (Array.isArray(prevRowsChildren) &&
            rowsChildren.length > prevRowsChildren.length) ||
          !Array.isArray(prevRowsChildren)
        ) {
          setHeight(datagridTableRef.current!.scrollHeight + 2); // +2 for border
        }
      }
    }, [datagridTableRef, rows.props.children, prevRows]);

    let initialPage = 1;
    let pageSize = 10;
    if (footer) {
      if (footer.props.pagination) {
        initialPage = footer.props.pagination.props.currentPage || 1;
        pageSize = footer.props.pagination.props.pageSize;
      }
    }

    const columnData = React.Children.map(filteredColumnChildren, column => {
      const { width = '100px', fixed } = column.props;
      return { width, fixed };
    });
    const initialFilters = columns.props.filters || [];
    const [page, setPage] = useState(initialPage);
    const [filters, setFilters] = useState<ColumnFilterState[]>(initialFilters);
    const [sort, setSort] = useState<ColumnSortState | undefined>(
      columns.props.sortState
    );

    const publishStateChange = useCallback(
      (changes: {
        paginationChanged?: boolean;
        filterChanged?: boolean;
        sortChanged?: boolean;
      }) => {
        const { paginationChanged, filterChanged, sortChanged } = changes;
        if (paginationChanged || filterChanged || sortChanged) {
          if (onStateChange) {
            const paginationData: PaginationStateChange = {
              changed: !!paginationChanged,
              data: {
                pageSize,
                currentPage: page
              }
            };
            const filterData: FilterStateChange = {
              changed: !!filterChanged,
              applied: filters.filter(f => f.applied).length > 0,
              data: filters
            };
            const sortData: SortStateChange = {
              changed: !!sortChanged,
              applied: !!sort,
              data: sort
            };
            onStateChange(paginationData, filterData, sortData);
          }
        }
      },
      [onStateChange, page, filters, sort, pageSize]
    );

    const prevPage = usePrevious(page);
    const prevSort = usePrevious(sort);
    const prevFilters = usePrevious(filters);

    useEffect(() => {
      const paginationChanged = prevPage !== undefined && prevPage !== page;
      const sortChanged = prevSort !== undefined && !deepEquals(prevSort, sort);
      const filterChanged =
        prevFilters !== undefined &&
        !deepEquals(
          prevFilters
            .filter(f => f.applied)
            .sort((fa, fb) => fa.colIndex - fb.colIndex)
            .map(f => f.value),
          filters
            .filter(f => f.applied)
            .sort((fa, fb) => fa.colIndex - fb.colIndex)
            .map(f => f.value)
        );
      publishStateChange({
        paginationChanged,
        sortChanged,
        filterChanged
      });
    }, [
      prevPage,
      prevSort,
      prevFilters,
      page,
      sort,
      filters,
      publishStateChange
    ]);

    const onFirstPage = useCallback(() => {
      setPage(1);
      closeDetailPaneHandler();
    }, []);
    const onLastPage = useCallback((lastPage: number) => {
      setPage(lastPage);
      closeDetailPaneHandler();
    }, []);
    const onNextPage = useCallback(() => {
      setPage(p => p + 1);
      closeDetailPaneHandler();
    }, []);
    const onPreviousPage = useCallback(() => {
      setPage(p => p - 1);
      closeDetailPaneHandler();
    }, []);
    const onPage = useCallback((currentPage: number) => {
      setPage(currentPage);
      closeDetailPaneHandler();
    }, []);

    const onApplyFilter = useCallback((colIndex: number, filterValue: any) => {
      setFilters(prev => {
        const i = prev.findIndex(f => f.colIndex === colIndex);
        return i === -1
          ? [
              ...prev,
              {
                colIndex,
                value: filterValue,
                popupOpen: false,
                applied: filterValue !== ''
              }
            ]
          : [
              ...prev.slice(0, i),
              {
                ...prev[i],
                value: filterValue,
                applied: filterValue !== ''
              },
              ...prev.slice(i + 1)
            ];
      });
    }, []);

    const onClearFilter = useCallback(
      (colIndex: number) =>
        setFilters(prev => {
          const i = prev.findIndex(f => f.colIndex === colIndex);
          return prev.map((f, idx) =>
            i === idx ? { ...f, value: undefined, applied: false } : f
          );
        }),
      []
    );

    const onOpenPopup = useCallback(
      (colIndex: number) =>
        setFilters(previousFilters => {
          const i = previousFilters.findIndex(f => f.colIndex === colIndex);
          return i === -1
            ? [
                ...previousFilters.map((f, idx) => ({
                  ...f,
                  popupOpen: i === idx ? true : false
                })),
                { colIndex, value: undefined, popupOpen: true, applied: false }
              ]
            : previousFilters.map((f, idx) => ({
                ...f,
                popupOpen: i === idx ? true : false
              }));
        }),
      []
    );

    const onClosePopup = useCallback(
      (colIndex: number) =>
        setFilters(previousFilters => {
          const i = previousFilters.findIndex(f => f.colIndex === colIndex);
          return previousFilters.map((f, idx) =>
            i === idx ? { ...f, popupOpen: false } : f
          );
        }),
      []
    );

    const onSortDirectionChange = useCallback(
      (colIndex: number, direction: 'asc' | 'desc') =>
        setSort({ colIndex, direction, applied: true }),
      []
    );

    const onSingleRowSelectionChange = (rowKey?: string) =>
      singleSelection &&
      singleSelection.onChange &&
      singleSelection.onChange(rowKey);

    const onMultiRowSelectionChange = (rowKey: string, checked: boolean) =>
      multiSelection &&
      multiSelection.onChange &&
      multiSelection.selected &&
      multiSelection.onChange(
        checked
          ? [...multiSelection.selected, rowKey]
          : multiSelection.selected.filter(r => r !== rowKey)
      );

    const getAllDatagridRowKeys = (): string[] => {
      const rowKeyList: string[] = [];
      if (rows.props.children) {
        React.Children.map(rows.props.children, child => {
          if (child && child.props.rowKey && !child.props.disableSelection) {
            rowKeyList.push(child.props.rowKey);
          }
        });
      }
      return rowKeyList;
    };

    const onSelectAllRowCheckboxChange = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      const { checked } = event.target;
      if (multiSelection && multiSelection.onChange) {
        const existingSelected = multiSelection.selected || [];
        const allRowKeys = getAllDatagridRowKeys();
        const newSelected = checked
          ? existingSelected
              .filter(e => allRowKeys.findIndex(r => r === e) === -1)
              .concat(allRowKeys)
          : existingSelected.filter(
              e => allRowKeys.findIndex(r => r === e) === -1
            );
        multiSelection.onChange(newSelected);
      }
    };

    const computeFilterProps = (colIndex: number, filterProps: FilterProps) => {
      const { applied, open, value } = filterProps;
      const applyFilter = (filterValue: any) =>
        onApplyFilter(colIndex, filterValue);
      const clearFilter = () => onClearFilter(colIndex);
      const closePopup = () => onClosePopup(colIndex);
      const openPopup = () => onOpenPopup(colIndex);

      const columnFilter = filters.find(f => f.colIndex === colIndex);

      return {
        applied: columnFilter ? columnFilter.applied : applied || false,
        open: columnFilter ? columnFilter.popupOpen : open || false,
        value: columnFilter ? columnFilter.value : value || false,
        applyFilter,
        clearFilter,
        closePopup,
        openPopup
      };
    };

    const computeSortProps = (colIndex: number, columnProps: ColumnProps) => {
      if (columnProps.sort && columnProps.sort.enable) {
        const sortDirectChange = (direction: 'asc' | 'desc') =>
          onSortDirectionChange(colIndex, direction);
        const sortForThisColumn =
          sort && sort.colIndex === colIndex ? sort : undefined;

        return {
          enable: columnProps.sort ? columnProps.sort.enable : false,
          label: columnProps.sort ? columnProps.sort.label : '',
          applied: sortForThisColumn ? sortForThisColumn.applied : false,
          direction: sortForThisColumn
            ? sortForThisColumn.direction
            : undefined,
          directionChange: sortDirectChange
        };
      }
    };

    const computeSelectionProps = (rowKey?: string) => {
      if (!rowKey) {
        // `rowKey` is a mandatory param for the selection feature to work.
        return;
      }
      if (singleSelection) {
        return {
          checked: singleSelection.selected === rowKey,
          type: 'radio' as 'radio' | 'checkbox',
          selectionChange: (e: React.ChangeEvent<HTMLInputElement>) =>
            onSingleRowSelectionChange(e.target.checked ? rowKey : undefined)
        };
      } else if (multiSelection) {
        return {
          checked: !!(
            multiSelection.selected &&
            multiSelection.selected.findIndex(s => s === rowKey) !== -1
          ),
          type: 'checkbox' as 'radio' | 'checkbox',
          selectionChange: (e: React.ChangeEvent<HTMLInputElement>) =>
            onMultiRowSelectionChange(rowKey, e.target.checked)
        };
      }
    };

    const computeAllRowsSelected = () => {
      const selected: string[] =
        multiSelection && multiSelection.selected
          ? multiSelection.selected
          : [];
      const allRowKeys = getAllDatagridRowKeys();
      return (
        selected.length > 0 &&
        allRowKeys.length > 0 &&
        allRowKeys.findIndex(r => selected.findIndex(s => s === r) === -1) ===
          -1
      );
    };

    const renderPlaceholder = () => {
      const empty = Array.isArray(rows.props.children)
        ? rows.props.children.length === 0
        : !rows.props.children;
      if (placeholder) {
        return React.cloneElement(placeholder, {
          empty
        });
      } else {
        return (
          <DatagridPlaceholder empty={empty}>
            No items to show..
          </DatagridPlaceholder>
        );
      }
    };

    const renderSelectionColumn = () => {
      const rowSelectionEnabled = multiSelection || singleSelection;
      const allRowsSelected = computeAllRowsSelected();

      return (
        <>
          {rowSelectionEnabled && (
            <DatagridColumn fixed={true}>
              {multiSelection && (
                <BasicCheckbox
                  attrs={{ 'aria-label': 'Select All Rows' }}
                  checked={allRowsSelected}
                  onChange={onSelectAllRowCheckboxChange}
                />
              )}
            </DatagridColumn>
          )}
        </>
      );
    };

    const renderColumns = () => {
      return React.cloneElement<ColumnsProps>(columns, {
        children: React.Children.map(
          filteredColumnChildren,
          (column, index) => {
            if (
              (detailPaneStatus.status && index < 1) ||
              !detailPaneStatus.status
            ) {
              return React.cloneElement(column, {
                sort: computeSortProps(index, column.props),
                filter:
                  column.props.filter &&
                  React.cloneElement(
                    column.props.filter,
                    computeFilterProps(index, column.props.filter.props)
                  ),
                isLastColumn:
                  React.Children.count(filteredColumnChildren) - 1 === index
              });
            }
          }
        ),
        clearSort: () => setSort(undefined)
      });
    };

    const renderRows = () => {
      if (rows.props.children) {
        return React.cloneElement<RowsProps>(rows, {
          children: React.Children.map(rows.props.children, row => {
            return row
              ? React.cloneElement(row, {
                  columnData,
                  expandable,
                  selection: computeSelectionProps(row.props.rowKey),
                  detailPane: getDetailPaneAttributes(row.props.rowKey)
                })
              : undefined;
          })
        });
      }
    };

    const renderFooter = () => {
      return (
        footer &&
        React.cloneElement<FooterProps>(footer, {
          multiSelectCount:
            multiSelection && multiSelection.selected
              ? multiSelection.selected.length
              : undefined,
          pagination:
            footer.props.pagination &&
            React.cloneElement<PaginationProps>(footer.props.pagination, {
              currentPage: page,
              onFirstPage,
              onLastPage,
              onNextPage,
              onPreviousPage,
              onPage,
              isDetailPaneOpen:
                detailPaneStatus.status && detailPaneStatus.key ? true : false
            }),
          isDetailPaneOpen:
            detailPaneStatus.status && detailPaneStatus.key ? true : false
        })
      );
    };

    // detail pane methods

    const renderDetailPane = () => {
      let detail;
      React.Children.forEach(detailPane?.props.children, detailPaneItem => {
        if (detailPaneItem?.props?.rowKey === detailPaneStatus.key) {
          detail = React.cloneElement(detailPaneItem, {
            closeDetailPaneHandler: closeDetailPaneHandler
          });
        }
      });
      return detail;
    };

    const renderDetailPaneButtonColumn = () => {
      return (
        <>
          {detailPane && (
            <DatagridColumn fixed={true}>
              <div></div>
            </DatagridColumn>
          )}
        </>
      );
    };

    const closeDetailPaneHandler = () => {
      setDetailPaneStatus({ status: false, key: '' });
    };

    const detailPaneButtonHandler = (rowKey: string | undefined) => {
      if (rowKey) {
        if (detailPaneStatus.key === rowKey) {
          setDetailPaneStatus(p => ({
            ...p,
            status: !p.status
          }));
        } else {
          setDetailPaneStatus({
            status: true,
            key: rowKey
          });
        }
      }
    };

    const getDetailPaneAttributes = (rowKey: string | undefined) => {
      if (detailPane && detailPane.props.children && rowKey) {
        return {
          open: detailPaneStatus.status,
          detailHandler: detailPaneButtonHandler.bind(null, rowKey),
          rowKey: detailPaneStatus.key
        };
      }
    };

    //

    return (
      <div
        ref={ref}
        className={cn('datagrid-host', {
          'datagrid-detail-open': detailPaneStatus.status
        })}>
        <div className="datagrid-outer-wrapper">
          <div className="datagrid-inner-wrapper">
            <div className="datagrid">
              <div ref={datagridTableRef} className="datagrid-table-wrapper">
                <div className="datagrid-table" title={title || ''} role="grid">
                  <div className="datagrid-header" role="rowgroup">
                    <div className="datagrid-row" role="row">
                      <div className="datagrid-row-master datagrid-row-flex">
                        <div className="datagrid-row-sticky">
                          {renderSelectionColumn()}
                        </div>
                        <div className="datagrid-row-sticky">
                          {renderDetailPaneButtonColumn()}
                        </div>
                        <div className="datagrid-row-scrollable">
                          {expandable && <DatagridColumn fixed={true} />}
                          {renderColumns()}
                        </div>
                      </div>
                    </div>
                  </div>
                  {renderRows()}
                  {!loading && renderPlaceholder()}
                </div>
              </div>
            </div>
            {renderFooter()}
            {loading && (
              <div className="datagrid-spinner">
                <Spinner size="md" />
              </div>
            )}
          </div>
          {detailPane && detailPaneStatus.status && (
            <div className="datagrid-detail-pane">{renderDetailPane()}</div>
          )}
        </div>
      </div>
    );
  }
);

type PlaceholderProps = PropsWithChildren<{
  empty?: boolean;
}>;

export const DatagridPlaceholder = React.forwardRef<
  HTMLDivElement,
  PlaceholderProps
>((props, ref) => {
  const { children, empty } = props;
  return (
    <div ref={ref} className="datagrid-placeholder-container">
      <div className={cn('datagrid-placeholder', { 'datagrid-empty': empty })}>
        {empty && (
          <>
            <div className="datagrid-placeholder-image" />
            {children}
          </>
        )}
      </div>
    </div>
  );
});
